import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Briefcase, 
  MapPin, 
  Clock, 
  PlusCircle,
  Loader2,
  MoreVertical,
  Edit,
  Trash2,
  Eye
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { getEmployerJobs, deleteJob } from '../../services/api';
import { toast } from 'sonner';

const EmployerJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const res = await getEmployerJobs();
      setJobs(res.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Möchten Sie diese Stellenanzeige wirklich löschen?')) return;
    
    try {
      await deleteJob(jobId);
      setJobs(jobs.filter(j => j.job_id !== jobId));
      toast.success('Stellenanzeige gelöscht');
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="employer-jobs-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground">Meine Stellenanzeigen</h1>
            <p className="text-muted-foreground mt-1">
              {jobs.length} Stellenanzeigen
            </p>
          </div>
          <Link to="/employer/jobs/new">
            <Button className="rounded-full gap-2" data-testid="new-job-btn">
              <PlusCircle className="w-4 h-4" />
              Neue Stelle
            </Button>
          </Link>
        </div>

        {/* Jobs List */}
        {jobs.length > 0 ? (
          <div className="grid gap-4">
            {jobs.map((job) => (
              <Card 
                key={job.job_id} 
                className="hover:shadow-md transition-all"
                data-testid={`employer-job-${job.job_id}`}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-foreground text-lg">{job.title}</h3>
                          <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {job.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Briefcase className="w-4 h-4" />
                              {job.job_type}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {new Date(job.created_at).toLocaleDateString('de-DE')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            job.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {job.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                          </span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem asChild>
                                <Link to={`/jobs/${job.job_id}`} className="flex items-center gap-2">
                                  <Eye className="w-4 h-4" />
                                  Ansehen
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/employer/jobs/${job.job_id}/edit`} className="flex items-center gap-2">
                                  <Edit className="w-4 h-4" />
                                  Bearbeiten
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(job.job_id)}
                                className="text-destructive flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Briefcase className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-semibold text-foreground mb-2">Keine Stellenanzeigen</h3>
              <p className="text-muted-foreground">
                Erstellen Sie Ihre erste Stellenanzeige
              </p>
              <Link to="/employer/jobs/new">
                <Button className="mt-4 rounded-full">Erste Stelle erstellen</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default EmployerJobs;
